/*
 * Sheets of QR labels as a real PDF file, with no PDF library.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY BY HAND
 * ────────────────────────────────────────────────────────────────────────
 *
 * A label sheet is the simplest PDF there is: rectangles and one line of
 * monospace text, on A4 pages. A QR code is a grid of dark squares, and a PDF
 * can draw squares natively -- so each code is VECTOR, sharp at any printer
 * resolution, with no image encoding at all. The standard Courier font is
 * built into every PDF reader, so nothing is embedded either. That is a
 * couple of hundred lines against a 300 kB+ dependency that would be fetched
 * for one button.
 *
 * Everything written is 7-bit ASCII (codes are [A-Z0-9-]; anything else in a
 * caption becomes '?'), so a JavaScript string's length IS its byte length,
 * and the cross-reference offsets below are exact.
 *
 * The QR always encodes the Isolate ID (the ISO code). A specimen's short lab
 * code (L-00042, migration 0054) is printed as a line of text under the code,
 * a size up, for the person reading the tube -- never what a scanner reads.
 *
 * The matrix comes from the `qrcode` package the app already uses for its
 * PNG labels, so the PDF and the on-screen label are the same code.
 */
import QRCode from 'qrcode';

const MM = 72 / 25.4;

/** Label grids on A4 portrait (210 x 297 mm). */
export const LAYOUTS = {
  // 24 a sheet: the common 3 x 8 address-label sheet; tubes and bags.
  a4_3x8: { cols: 3, rows: 8, margin: 10, gap: 2, font: 7 },
  // 10 a sheet: large labels for specimen bags in the field.
  a4_2x5: { cols: 2, rows: 5, margin: 12, gap: 4, font: 10 },
};

const PAGE = { w: 210, h: 297 };

const num = (n) => {
  const s = (Math.round(n * 100) / 100).toFixed(2);
  return s.replace(/\.?0+$/, '') || '0';
};

/** Text that is safe inside a PDF string literal: ASCII, parens escaped. */
export function pdfText(text) {
  return String(text ?? '')
    .replace(/[^ -~]/g, '?')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

/** The code's dark modules as rectangles: one per horizontal run. */
function qrOps(text, x, yTop, size) {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const n = modules.size;
  const s = size / n;
  const ops = [];
  for (let row = 0; row < n; row += 1) {
    let start = -1;
    for (let col = 0; col <= n; col += 1) {
      const dark = col < n && modules.get(row, col);
      if (dark && start < 0) start = col;
      if (!dark && start >= 0) {
        ops.push(`${num(x + start * s)} ${num(yTop - (row + 1) * s)} ${num((col - start) * s)} ${num(s)} re`);
        start = -1;
      }
    }
  }
  return `0 g\n${ops.join('\n')}\nf\n`;
}

/** A code split where a person reads it: "ISO-20261012-0930" / "SV8WR-001". */
export function codeLines(code) {
  const parts = String(code).split('-');
  if (parts.length >= 5 && parts[0] === 'ISO') {
    return [parts.slice(0, 3).join('-'), parts.slice(3).join('-')];
  }
  if (code.length <= 18) return [code];
  return [code.slice(0, 18), code.slice(18)];
}

function labelOps(label, cell, layout) {
  const pad = 2 * MM;
  const qrSize = Math.min(cell.h - 2 * pad, cell.w * 0.42);
  const qrX = cell.x + pad;
  const top = cell.y + cell.h - pad;
  let out = '';
  // A faint cut guide, so a plain sheet can be cut by hand.
  out += `q 0.8 G 0.25 w ${num(cell.x)} ${num(cell.y)} ${num(cell.w)} ${num(cell.h)} re S Q\n`;
  out += qrOps(label.code, qrX, top, qrSize);
  const textX = qrX + qrSize + pad;
  const size = layout.font;
  const lines = [...codeLines(label.code)];
  let y = top - size * 1.1;
  out += 'BT\n';
  lines.forEach((line, i) => {
    out += `/F1 ${size} Tf ${num(textX)} ${num(y)} Td (${pdfText(line)}) Tj\n`;
    out += `${num(-textX)} ${num(-y)} Td\n`;
    y -= size * 1.25;
    if (i === lines.length - 1) y -= size * 0.25;
  });
  if (label.lab_code) {
    // Short (L- and 5 digits for years), so a size up still fits beside the QR.
    const big = size * 1.3;
    y -= big - size;
    out += `/F1 ${num(big)} Tf ${num(textX)} ${num(y)} Td (${pdfText(label.lab_code)}) Tj\n`;
    out += `${num(-textX)} ${num(-y)} Td\n`;
    y -= big * 1.25;
  }
  if (label.caption) {
    const small = Math.max(5, size - 2);
    // Cut to what fits beside the code (Helvetica averages ~0.55 em), and cut
    // BEFORE escaping, so a slice can never end inside an escape sequence.
    const room = Math.max(4, Math.floor((cell.x + cell.w - pad - textX) / (small * 0.55)));
    out += `/F2 ${small} Tf ${num(textX)} ${num(y)} Td (${pdfText(String(label.caption).slice(0, room))}) Tj\n`;
  }
  out += 'ET\n';
  return out;
}

/**
 * The PDF document, as a string of ASCII.
 *   labels: [{ code, lab_code?, caption? }]   one label per entry, in order;
 *           `code` is the QR and the text beside it, `lab_code` a line of its own
 *   layout: a key of LAYOUTS
 */
export function buildLabelPdf(labels, { layout = 'a4_3x8', title = 'Specimen labels' } = {}) {
  const grid = LAYOUTS[layout];
  if (!grid) throw new Error(`unknown label layout: ${layout}`);
  const perPage = grid.cols * grid.rows;
  const cellW = ((PAGE.w - 2 * grid.margin) - (grid.cols - 1) * grid.gap) / grid.cols;
  const cellH = ((PAGE.h - 2 * grid.margin) - (grid.rows - 1) * grid.gap) / grid.rows;
  const pages = [];
  for (let i = 0; i < Math.max(labels.length, 1); i += perPage) pages.push(labels.slice(i, i + perPage));

  // Objects: 1 catalog, 2 pages, 3 Courier-Bold, 4 Helvetica, 5 info,
  // then a page and its content stream for each page.
  const objects = [];
  const pageIds = pages.map((_, i) => 6 + 2 * i);
  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;
  objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold >>';
  objects[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';
  objects[5] = `<< /Title (${pdfText(title)}) /Producer (PITRIOT) >>`;
  pages.forEach((page, p) => {
    let content = '';
    page.forEach((label, i) => {
      const col = i % grid.cols;
      const row = Math.floor(i / grid.cols);
      const xMm = grid.margin + col * (cellW + grid.gap);
      const yMm = PAGE.h - grid.margin - (row + 1) * cellH - row * grid.gap;
      content += labelOps(label, { x: xMm * MM, y: yMm * MM, w: cellW * MM, h: cellH * MM }, grid);
    });
    objects[pageIds[p]] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(PAGE.w * MM)} ${num(PAGE.h * MM)}] `
      + `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${pageIds[p] + 1} 0 R >>`;
    objects[pageIds[p] + 1] = `<< /Length ${content.length} >>\nstream\n${content}endstream`;
  });

  let pdf = '%PDF-1.4\n';
  const offsets = [];
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let id = 1; id < objects.length; id += 1) pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R /Info 5 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return pdf;
}

export function labelPdfBlob(labels, options) {
  return new Blob([buildLabelPdf(labels, options)], { type: 'application/pdf' });
}

/** Save the sheet as a file. Returns the Blob (tests read it back). */
export function downloadLabelPdf(labels, filename, options) {
  const blob = labelPdfBlob(labels, options);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'specimen-labels.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return blob;
}
